import { Ionicons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import React, { useMemo, useState } from 'react';
import { Pressable, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { PlayerAvatar } from '@/components/match/PlayerRatings';
import { TeamCrest } from '@/components/matches/TeamCrest';
import { AppText } from '@/components/ui/AppText';
import { Segmented } from '@/components/ui/Segmented';
import { useThemePalette } from '@/config/theme';
import type { FixtureWithRefs, MatchEventWithRefs } from '@/lib/data/provider';
import { localizeDigits } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import { localizeTeamName } from '@/lib/i18n/footballNames';
import type { MatchEventType } from '@/lib/supabase/types';

const GOAL_TYPES: ReadonlySet<MatchEventType> = new Set(['goal', 'penalty', 'own_goal']);
const KEY_TYPES: ReadonlySet<MatchEventType> = new Set(['goal', 'penalty', 'own_goal', 'red']);

type Filter = 'all' | 'key';

function eventLabel(type: MatchEventType): string {
  switch (type) {
    case 'goal':
      return 'match.goal';
    case 'own_goal':
      return 'match.ownGoal';
    case 'penalty':
      return 'match.penalty';
    case 'yellow':
      return 'match.yellowCard';
    case 'red':
      return 'match.redCard';
    case 'sub':
      return 'match.substitution';
    case 'var':
      return 'match.var';
  }
}

/** The icon (or card chip) that sits on the timeline spine for an event. */
function EventNode({ type }: { type: MatchEventType }) {
  const palette = useThemePalette();
  if (type === 'yellow' || type === 'red') {
    return (
      <View
        style={{ width: 9, height: 12, borderRadius: 2, backgroundColor: type === 'red' ? palette.loss : '#F4D03F' }}
      />
    );
  }
  if (type === 'sub') return <Ionicons name="swap-vertical" size={14} color={palette.soft} />;
  if (type === 'var') return <Ionicons name="videocam-outline" size={13} color={palette.soft} />;
  // goal / penalty / own goal
  const color = type === 'own_goal' ? palette.loss : palette.voltBright;
  return <Ionicons name="football" size={14} color={color} />;
}

/** A centred divider pill (half time / full time) carrying the score at the break. */
function ScoreDivider({ label, home, away }: { label: string; home: number; away: number }) {
  const { arabicNumerals, isRTL } = useLocale();
  return (
    <View className="my-1 flex-row items-center gap-3 px-4">
      <View className="h-px flex-1 bg-line" />
      <View className="flex-row items-center gap-2 rounded-full border border-line bg-surface px-3 py-1">
        <AppText variant="micro" weight="bold" className="uppercase tracking-wide text-soft">
          {label}
        </AppText>
        <AppText variant="micro" weight="bold" tabular className="text-muted">
          {`⁦${localizeDigits(isRTL ? `${away} - ${home}` : `${home} - ${away}`, arabicNumerals)}⁩`}
        </AppText>
      </View>
      <View className="h-px flex-1 bg-line" />
    </View>
  );
}

/** One timeline row: a node on the spine + the event content beside it. */
function EventRow({
  event,
  fixture,
  score,
  isFirst,
  isLast,
}: {
  event: MatchEventWithRefs;
  fixture: FixtureWithRefs;
  score: { h: number; a: number } | null;
  isFirst: boolean;
  isLast: boolean;
}) {
  const palette = useThemePalette();
  const router = useRouter();
  const { t } = useTranslation();
  const { arabicNumerals, locale, isRTL } = useLocale();
  const isGoal = GOAL_TYPES.has(event.type);
  const ringColor = isGoal ? palette.voltLine : palette.line;

  const eventTeam =
    event.team?.id === fixture.home_team?.id
      ? fixture.home_team
      : event.team?.id === fixture.away_team?.id
        ? fixture.away_team
        : event.team;
  const teamName = eventTeam ? localizeTeamName(eventTeam, locale, true) : undefined;
  const teamLogo =
    event.team?.id === fixture.home_team?.id
      ? fixture.home_team?.logo_url
      : event.team?.id === fixture.away_team?.id
        ? fixture.away_team?.logo_url
        : null;

  const playerName = event.player?.known_as ?? event.player?.name;
  const inName = event.assist?.known_as ?? event.assist?.name; // sub: player coming on
  const assistName = event.assist?.known_as ?? event.assist?.name; // goal: the assister

  return (
    <Pressable
      accessibilityRole={event.player ? 'button' : undefined}
      onPress={() => event.player && router.push(`/player/${event.player.id}`)}
      className="flex-row gap-3 px-4 active:opacity-70"
    >
      {/* spine + node */}
      <View className="items-center" style={{ width: 30 }}>
        <View style={{ width: 2, height: 8, backgroundColor: isFirst ? 'transparent' : palette.line }} />
        <View
          className="items-center justify-center rounded-full border bg-inset"
          style={{ width: 28, height: 28, borderColor: ringColor }}
        >
          <EventNode type={event.type} />
        </View>
        <View style={{ width: 2, flex: 1, backgroundColor: isLast ? 'transparent' : palette.line }} />
      </View>

      {/* content */}
      <View className="flex-1 pb-3.5 pt-0.5">
        <View className="flex-row items-center gap-2">
          <AppText variant="micro" weight="bold" tabular className={isGoal ? 'text-volt-bright' : 'text-soft'}>
            {localizeDigits(`${event.minute}'`, arabicNumerals)}
          </AppText>
          {isGoal && score ? (
            <View className="rounded bg-volt-glass px-1.5 py-0.5">
              <AppText variant="micro" weight="bold" tabular className="text-volt-bright">
                {`⁦${localizeDigits(isRTL ? `${score.a} - ${score.h}` : `${score.h} - ${score.a}`, arabicNumerals)}⁩`}
              </AppText>
            </View>
          ) : null}
          {teamLogo ? (
            <TeamCrest uri={teamLogo} name={eventTeam ? localizeTeamName(eventTeam, locale) : '?'} size="xs" />
          ) : null}
          {teamName ? (
            <AppText variant="micro" className="text-muted" numberOfLines={1}>
              {teamName}
            </AppText>
          ) : null}
        </View>

        {event.type === 'sub' ? (
          <View className="mt-1.5 flex-row items-center gap-2">
            {/* IN: green arrow + photo + name */}
            <View className="flex-1 flex-row items-center gap-1.5">
              <Ionicons name="arrow-up-circle" size={15} color={palette.win} />
              <PlayerAvatar uri={event.assist?.photo_url} name={inName ?? '?'} size={22} />
              <AppText variant="micro" weight="semibold" className="flex-1 text-ink" numberOfLines={1}>
                {inName ?? '-'}
              </AppText>
            </View>
            <Ionicons name="swap-horizontal" size={13} color={palette.soft} />
            {/* OUT: name + photo + red arrow */}
            <View className="flex-1 flex-row items-center justify-end gap-1.5">
              <AppText variant="micro" className="flex-1 text-end text-muted" numberOfLines={1}>
                {playerName ?? '-'}
              </AppText>
              <PlayerAvatar uri={event.player?.photo_url} name={playerName ?? '?'} size={22} />
              <Ionicons name="arrow-down-circle" size={15} color={palette.loss} />
            </View>
          </View>
        ) : (
          <View className="mt-1 flex-row items-start gap-2.5">
            {/* Scorer / booked player's photo (365-style). Only when we have the
                player; falls back cleanly to the event label otherwise. */}
            {event.player ? (
              <PlayerAvatar uri={event.player.photo_url} name={playerName ?? '?'} size={32} />
            ) : null}
            <View className="flex-1 gap-0.5">
              {/* Headline: the scorer / booked player (falls back to the event
                  type when API-Football gives no player). */}
              <AppText
                variant="caption"
                weight="bold"
                className={isGoal ? 'text-volt-bright' : 'text-ink'}
                numberOfLines={1}
                style={{ textAlign: isRTL ? 'right' : 'left' }}
              >
                {playerName ?? t(eventLabel(event.type))}
              </AppText>
              {/* Type qualifier only when it adds detail beyond the icon: a
                  penalty / own goal / card kind, never a redundant "Goal". */}
              {playerName && event.type !== 'goal' ? (
                <AppText variant="micro" className="text-soft" numberOfLines={1}>
                  {t(eventLabel(event.type))}
                </AppText>
              ) : null}
              {/* Assist, with the assister's photo, on goals. */}
              {isGoal && assistName ? (
                <View className="mt-0.5 flex-row items-center gap-1.5">
                  <PlayerAvatar uri={event.assist?.photo_url} name={assistName} size={18} />
                  <AppText variant="micro" className="text-muted" numberOfLines={1}>
                    {t('match.assistBy', { name: assistName })}
                  </AppText>
                </View>
              ) : null}
            </View>
          </View>
        )}
      </View>
    </Pressable>
  );
}

/**
 * 365-style match timeline: a vertical spine with goal / card / substitution
 * nodes, running score on goals, a half-time score divider, and an All vs
 * Highlights filter. Most-recent first.
 */
export function EventTimeline({
  events,
  fixture,
}: {
  events: MatchEventWithRefs[];
  fixture: FixtureWithRefs;
}) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<Filter>('all');

  // Running score (home/away) after each event, computed chronologically.
  const { scoreById, htScore } = useMemo(() => {
    const homeId = fixture.home_team?.id;
    const awayId = fixture.away_team?.id;
    const asc = [...events].sort((a, b) => a.minute - b.minute);
    const map = new Map<string, { h: number; a: number }>();
    let h = 0;
    let a = 0;
    let ht: { h: number; a: number } | null = null;
    for (const event of asc) {
      if (event.type === 'goal' || event.type === 'penalty') {
        if (event.team?.id === homeId) h += 1;
        else if (event.team?.id === awayId) a += 1;
      } else if (event.type === 'own_goal') {
        if (event.team?.id === homeId) a += 1;
        else if (event.team?.id === awayId) h += 1;
      }
      map.set(event.id, { h, a });
      if (event.minute <= 45) ht = { h, a };
    }
    return { scoreById: map, htScore: ht };
  }, [events, fixture.home_team?.id, fixture.away_team?.id]);

  const shown = useMemo(
    () => (filter === 'key' ? events.filter((event) => KEY_TYPES.has(event.type)) : events),
    [events, filter],
  );

  const ordered = useMemo(() => [...shown].sort((a, b) => b.minute - a.minute), [shown]);
  const secondHalf = ordered.filter((event) => event.minute > 45);
  const firstHalf = ordered.filter((event) => event.minute <= 45);
  const showHalfDivider = firstHalf.length > 0 && secondHalf.length > 0 && htScore != null;

  const renderRow = (event: MatchEventWithRefs, index: number, list: MatchEventWithRefs[]) => (
    <EventRow
      key={event.id}
      event={event}
      fixture={fixture}
      score={GOAL_TYPES.has(event.type) ? (scoreById.get(event.id) ?? null) : null}
      isFirst={index === 0}
      isLast={index === list.length - 1}
    />
  );

  return (
    <View className="gap-2">
      <View className="px-4">
        <Segmented
          options={[
            { value: 'all', label: t('match.timelineAll') },
            { value: 'key', label: t('match.timelineKey') },
          ]}
          value={filter}
          onChange={setFilter}
        />
      </View>

      {showHalfDivider ? (
        <>
          {secondHalf.map((event, index) => renderRow(event, index, secondHalf))}
          <ScoreDivider label={t('match.halfTime')} home={htScore.h} away={htScore.a} />
          {firstHalf.map((event, index) => renderRow(event, index, firstHalf))}
        </>
      ) : (
        ordered.map((event, index) => renderRow(event, index, ordered))
      )}
    </View>
  );
}
